import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { Link } from '@tanstack/react-router';
import { Text } from '@chatsdet/ui';
import { formatRelativeTimeAgo } from '../../../../features/workflows/useAgenticRunDetail';
import type { RunsListRow } from '../../../../features/workflows/useWorkflowTestExecutions';
import { formatDurationFromIso } from '../../../../lib/formatDuration';
import { TestRunStatusCell } from '../../../components/TestRunStatusCell';
import { TestRunActionsMenu } from './TestRunActionsMenu';

interface UseTestRunColumnsArgs {
  isCancelPending: boolean;
  isDeletePending: boolean;
  onCancel: (executionId: string) => void;
  onDelete: (executionId: string) => void;
  /** External-run handlers — never the same endpoints as the native pair. */
  isExternalCancelPending: boolean;
  isExternalRerunPending: boolean;
  onExternalCancel: (runId: string) => void;
  onExternalRerun: (runId: string) => void;
}

export function useTestRunColumns({
  isCancelPending,
  isDeletePending,
  onCancel,
  onDelete,
  isExternalCancelPending,
  isExternalRerunPending,
  onExternalCancel,
  onExternalRerun,
}: UseTestRunColumnsArgs): ColumnDef<StockFeatures, RunsListRow>[] {
  return React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <TestRunStatusCell execution={row.original} />,
        enableSorting: true,
        meta: { flex: 2, minWidth: 380 },
      },
      {
        id: 'suite',
        header: 'Suite',
        cell: ({ row }) => {
          const execution = row.original;
          // `runTarget` is what this run was actually launched against —
          // named suite, named schedule, tag set, or ad hoc. Reading it
          // instead of `suiteName` alone fixes the column showing `—` for
          // most runs: a daily "All published" schedule now has a real
          // label. Native rows always carry `runTarget`; external rows have
          // no equivalent concept and fall back to their own suite fields.
          if (execution.kind === 'external') {
            const { suiteName, suiteId, runnerName, shardTotal, selection, rerunOfRunId } =
              execution;
            // Areas and re-runs are properties of the RUNNER, not the suite —
            // shown as a secondary line under the suite link (or in its place
            // when there is no suite, e.g. an ingest-API run).
            const secondary = rerunOfRunId
              ? `${runnerName ?? 'External run'} · ${
                  selection.specPath?.split('/').filter(Boolean).at(-1) ?? 'area'
                } (re-run)`
              : shardTotal && shardTotal > 1
                ? `${runnerName ?? 'External run'} · ${shardTotal} areas`
                : null;
            if (!suiteName || !suiteId) {
              return (
                <Text size="sm" className="text-muted-foreground">
                  {secondary ?? '—'}
                </Text>
              );
            }
            return (
              <div onClick={(e) => e.stopPropagation()} className="min-w-0">
                <Link
                  to="/suites/$suiteId"
                  params={{ suiteId }}
                  className="font-medium text-foreground hover:underline truncate block"
                >
                  {suiteName}
                </Link>
                {secondary && (
                  <Text size="xs" className="text-muted-foreground truncate block">
                    {secondary}
                  </Text>
                )}
              </div>
            );
          }
          const { runTarget, suiteId } = execution;
          if (runTarget.kind === 'suite' && suiteId) {
            return (
              <div onClick={(e) => e.stopPropagation()} className="min-w-0">
                <Link
                  to="/suites/$suiteId"
                  params={{ suiteId }}
                  className="font-medium text-foreground hover:underline truncate block"
                >
                  {runTarget.label}
                </Link>
              </div>
            );
          }
          return (
            <Text size="sm" className="text-muted-foreground truncate block">
              {runTarget.label}
            </Text>
          );
        },
        enableSorting: false,
        meta: { flex: 1.6, minWidth: 160 },
      },
      {
        id: 'triggeredBy',
        header: 'Run by',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground truncate block">
            {row.original.triggeredBy?.displayName ?? row.original.triggeredBy?.email ?? 'system'}
          </Text>
        ),
        enableSorting: false,
        meta: { flex: 0.7, minWidth: 96, maxWidth: 160 },
      },
      {
        accessorKey: 'startedAt',
        header: 'Started',
        cell: ({ row }) => {
          const startedAt = row.original.startedAt ?? row.original.createdAt;
          const duration = formatDurationFromIso(row.original.startedAt, row.original.finishedAt);
          return (
            <div className="flex flex-col">
              <Text size="xs" className="text-muted-foreground">
                {formatRelativeTimeAgo(startedAt)}
              </Text>
              {duration !== '\u2014' && (
                <Text size="xs" className="text-muted-foreground/60">
                  {duration}
                </Text>
              )}
            </div>
          );
        },
        enableSorting: true,
        meta: { flex: 0.6, minWidth: 88, maxWidth: 120 },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <TestRunActionsMenu
            execution={row.original}
            isCancelPending={isCancelPending}
            isDeletePending={isDeletePending}
            onCancel={onCancel}
            onDelete={onDelete}
            isExternalCancelPending={isExternalCancelPending}
            isExternalRerunPending={isExternalRerunPending}
            onExternalCancel={onExternalCancel}
            onExternalRerun={onExternalRerun}
          />
        ),
        meta: { flex: 0.3 },
      },
    ],
    [
      isCancelPending,
      isDeletePending,
      onCancel,
      onDelete,
      isExternalCancelPending,
      isExternalRerunPending,
      onExternalCancel,
      onExternalRerun,
    ]
  );
}
